import {
  Card,
  Link,
  SkeletonLine,
  Tooltip,
  Typography,
} from '@one-thd/sui-atomic-components';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import React, { useEffect, useState } from 'react';
import { getTotalSpend } from '../../api/oms/order-history';
import {
  useCommonContext,
  usePurchaseHistoryContext,
} from '../../hooks/useContexts';
import { ITotalSpendData } from '../../models/purchase-history/spending';

export const SpendOverview = () => {
  const { svocID, userID, isLoggedIn } = useThdCustomer();
  const { isDesktop } = useCommonContext();
  const {
    orderHistoryData,
    initResultsFilter,
    orderHistoryLoaded,
    customerType,
  } = usePurchaseHistoryContext();
  const [totalData, setTotalData] = useState<ITotalSpendData | null>(null);
  const [loading, setLoading] = useState(true);

  const totalSpendData = async () => {
    try {
      const request = {
        customerSpendRequest: {
          customerAccountId:
            orderHistoryData?.customerAccountId || svocID || '',
          groupType: 'YEAR',
          resultsFilter: initResultsFilter,
        },
      };
      const response = await getTotalSpend(request, userID);
      if (response.status === 200) {
        setTotalData(response?.data?.customerSpendByYear?.[0]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      totalSpendData();
    }
  }, [isLoggedIn]);

  if (
    orderHistoryLoaded &&
    customerType === 'b2b' &&
    orderHistoryData?.customerAccountId &&
    isDesktop
  ) {
    const grossSales = Number(totalData?.grossSales ?? 0)
      ?.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      ?.split('.')?.[0];
    const proxtraSpend = (totalData?.proxtraSpend ?? 0)
      ?.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      ?.split('.')?.[0];
    const savings = Math.abs(totalData?.savings ?? 0)
      ?.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      ?.split('.')?.[0];

    return (
      <Card data-testid="spendOverview" className="sui-w-1/2">
        <div className="sui-flex sui-justify-between">
          {loading ? (
            <div className="sui-w-6/12">
              <SkeletonLine variant="heading" fullWidth />
            </div>
          ) : (
            <Typography weight="bold" variant="h5">
              {totalData?.year ?? ''} Spend Overview
            </Typography>
          )}
          <div className="sui-flex sui-gap-[8px] sui-items-center">
            <img
              width={24}
              height={24}
              alt="Pro Extra Spend Ribbon"
              src="https://assets.thdstatic.com/images/v1/loyalty-badge.svg"
            />
            <Link
              href="https://www.homedepot.com/myaccount/proXtraRewards"
              variant="body-base"
            >
              Rewards History
            </Link>
          </div>
        </div>
        <div className="sui-flex sui-justify-between">
          <div>
            {(() => {
              if (loading) return <SkeletonLine variant="heading" />;
              return (
                <Typography variant="h5" style={{ fontSize: '20px' }}>
                  ${grossSales}
                </Typography>
              );
            })()}
            <Typography variant="body-base">Total Spend</Typography>
          </div>
          <div>
            {(() => {
              if (loading) return <SkeletonLine variant="heading" />;
              if (grossSales?.length > 12 && (proxtraSpend?.length ?? 0) > 7) {
                const modifiedProxtraSpend = `${
                  proxtraSpend?.split(',')[0]
                }.${proxtraSpend?.split(',')[1]?.slice(0, 1)}K`;

                return (
                  <Tooltip placement="bottom" title={`$${proxtraSpend}`}>
                    <Typography
                      variant="h5"
                      style={{ fontSize: '20px' }}
                      decoration="underline"
                    >
                      ${modifiedProxtraSpend}
                    </Typography>
                  </Tooltip>
                );
              }
              return (
                <Typography variant="h5" style={{ fontSize: '20px' }}>
                  ${proxtraSpend}
                </Typography>
              );
            })()}
            <Typography variant="body-base">ProXtra Loyalty Spend</Typography>
          </div>
          <div>
            {(() => {
              if (loading) return <SkeletonLine variant="heading" />;
              if (grossSales?.length > 12 && savings?.length > 7) {
                const modifiedSavings = `${savings?.split(',')[0]}.${savings
                  ?.split(',')[1]
                  ?.slice(0, 1)}K`;

                return (
                  <Tooltip placement="bottom" title={`$${savings}`}>
                    <Typography
                      variant="h5"
                      color="success"
                      style={{ fontSize: '20px' }}
                      decoration="underline"
                    >
                      ${modifiedSavings}
                    </Typography>
                  </Tooltip>
                );
              }
              return (
                <Typography
                  variant="h5"
                  color="success"
                  style={{ fontSize: '20px' }}
                >
                  ${savings}
                </Typography>
              );
            })()}
            <Typography variant="body-base">Total Savings</Typography>
          </div>
        </div>
      </Card>
    );
  }

  return null;
};
